import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, Phone, Send, CheckCircle, User, Building, MessageSquare, ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const QuoteRequest = () => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', service: '', message: '' })
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))

  const handleSubmit = (e) => {
    e.preventDefault()
    const subject = `Solicitação de cotação - ${formData.company || formData.name}`
    const body = [
      'Nova solicitação de cotação',
      '',
      `Nome: ${formData.name}`,
      `Email: ${formData.email}`,
      `Telefone: ${formData.phone}`,
      `Empresa: ${formData.company || 'Não informado'}`,
      `Serviço de interesse: ${formData.service}`,
      '',
      'Mensagem:',
      formData.message,
    ].join('\n')
    window.location.href = `mailto:comercial@agsgloballogistics.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    setIsSubmitted(true)
    setTimeout(() => {
      setIsSubmitted(false)
      setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' })
    }, 3000)
  }

  const services = [
    t('servicos.items.MAR.title'),
    t('servicos.items.AIR.title'),
    t('servicos.items.ROD.title'),
    t('servicos.items.ARM.title'),
    t('servicos.items.PRJ.title'),
    t('servicos.items.EMB.title'),
    t('servicos.items.SCM.title'),
  ]

  const inputClass = "w-full pl-12 pr-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all"
  const labelClass = "block text-sm font-semibold text-white/80 mb-2"

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: '#0C2D54' }}>
      <div className="container-custom py-16">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl mx-auto">

          <Link to="/contato" className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white transition-colors mb-8">
            <ArrowLeft size={16} /> {t('forms.back')}
          </Link>

          <div className="mb-10">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
              {t('cotacao.title')} <span style={{ color: '#38bdf8' }}>{t('cotacao.titleHighlight')}</span>
            </h1>
            <p className="text-white/65 text-base leading-relaxed">
              {t('cotacao.subtitle')}
            </p>
          </div>

          <div className="rounded-2xl p-8" style={{ backgroundColor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
            {isSubmitted ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">{t('forms.success')}</h3>
                <p className="text-white/65">{t('forms.quoteSuccess')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>{t('forms.name')} {t('forms.required')}</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                      <input type="text" name="name" value={formData.name} onChange={handleChange} required className={inputClass} placeholder={t('forms.namePlaceholder')} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>{t('forms.email')} {t('forms.required')}</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} placeholder={t('forms.emailPlaceholder')} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>{t('forms.phone')} {t('forms.required')}</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className={inputClass} placeholder={t('forms.phonePlaceholder')} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>{t('forms.company')}</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                      <input type="text" name="company" value={formData.company} onChange={handleChange} className={inputClass} placeholder={t('forms.companyPlaceholder')} />
                    </div>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>{t('forms.service')} {t('forms.required')}</label>
                  <select name="service" value={formData.service} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all">
                    <option value="" className="bg-[#0C2D54]">{t('forms.servicePlaceholder')}</option>
                    {services.map((s) => <option key={s} value={s} className="bg-[#0C2D54]">{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>{t('forms.message')} {t('forms.required')}</label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-4 text-white/40" size={20} />
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows="5" className={`${inputClass} resize-none`} placeholder={t('cotacao.messagePlaceholder')} />
                  </div>
                </div>
                <button type="submit" className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold transition-all duration-300 hover:brightness-110" style={{ backgroundColor: '#38bdf8', color: '#0C2D54' }}>
                  <Send size={20} />
                  {t('forms.sendQuote')}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default QuoteRequest
